import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { 
  Shield, 
  X, 
  Send, 
  Loader2,
  Sparkles,
  HelpCircle,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quickPrompts = [
  { label: 'Reset Ad ID', prompt: 'How do I reset my advertising ID on iPhone and Android?' },
  { label: 'Disable Location', prompt: 'How do I disable location tracking on my phone?' },
  { label: 'Set Up VPN', prompt: 'Help me choose and set up a VPN for privacy' },
  { label: 'Browser Privacy', prompt: 'How do I configure my browser for maximum privacy?' },
  { label: 'Check Data Breach', prompt: 'How can I check if my email has been in a data breach?' },
  { label: '2FA Setup', prompt: 'Guide me through setting up two-factor authentication' },
];

export default function FloatingAssistant({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [pulseAnimation, setPulseAnimation] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Stop pulse after 10 seconds
    const timer = setTimeout(() => setPulseAnimation(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const openChat = () => {
    setIsOpen(true);
    setIsMinimized(false);
    setPulseAnimation(false);
    
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `👋 Hi${user ? ` ${user.name}` : ''}! I'm your Privacy Assistant.\n\nI can help you with:\n• Setting up VPNs and privacy tools\n• Disabling tracking on your devices\n• Checking if your data has been breached\n• Understanding privacy threats\n\nWhat would you like help with?`
      }]);
    }
  };

  const sendMessage = async (messageText) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      if (!user) {
        // Not logged in - provide helpful response without API call
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I'd love to help you with that! To access our AI assistant with personalized guidance, please sign up for a free account.\n\nIn the meantime, here are some quick tips:\n\n📱 **Reset Advertising ID:**\n• iPhone: Settings → Privacy → Tracking → Reset\n• Android: Settings → Google → Ads → Reset\n\n🔒 **Quick Privacy Wins:**\n1. Disable location services for apps that don't need it\n2. Use a privacy-focused browser like Brave or Firefox\n3. Enable two-factor authentication everywhere\n\nWould you like to sign up for personalized help?`
        }]);
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again or rephrase your question.',
        error: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleQuickPrompt = (prompt) => {
    sendMessage(prompt);
  };

  if (!isOpen) {
    return (
      <button
        onClick={openChat}
        className={`fixed bottom-20 right-6 z-50 group ${pulseAnimation ? 'animate-bounce' : ''}`}
        data-testid="floating-assistant-btn"
      >
        {/* Pulse ring */}
        {pulseAnimation && (
          <div className="absolute inset-0 w-14 h-14 rounded-full bg-[#00FF94]/30 animate-ping" />
        )}
        
        {/* Main button */}
        <div className="relative w-14 h-14 rounded-full bg-[#00FF94] shadow-lg shadow-[#00FF94]/30 flex items-center justify-center group-hover:scale-110 transition-transform">
          <Shield className="w-7 h-7 text-black" />
        </div>
        
        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-[#0a0a0a] border border-[#262626] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          <span className="text-sm text-white">Need help?</span>
          <div className="absolute top-full right-4 w-2 h-2 bg-[#0a0a0a] border-r border-b border-[#262626] transform rotate-45 -mt-1" />
        </div>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 w-[380px] bg-[#0a0a0a] border border-[#262626] rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${isMinimized ? 'h-14' : 'h-[500px]'}`}
      data-testid="floating-assistant-chat"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 bg-[#00FF94] cursor-pointer"
        onClick={() => setIsMinimized(!isMinimized)}
      >
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-black" />
          <span className="font-medium text-black text-sm">Privacy Assistant</span>
          {loading && <Loader2 className="w-4 h-4 text-black animate-spin" />}
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
            className="p-1 hover:bg-black/10 rounded"
          >
            <ChevronUp className={`w-4 h-4 text-black transition-transform ${isMinimized ? 'rotate-180' : ''}`} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
            className="p-1 hover:bg-black/10 rounded"
          >
            <X className="w-4 h-4 text-black" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <ScrollArea className="h-[340px] p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div 
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
                    message.role === 'user'
                      ? 'bg-[#00FF94] text-black'
                      : message.error
                        ? 'bg-red-500/10 border border-red-500/20 text-gray-300'
                        : 'bg-[#171717] border border-[#262626] text-gray-300'
                  }`}>
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="px-3 py-2 bg-[#171717] border border-[#262626] rounded-xl flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-[#00FF94] animate-spin" />
                    <span className="text-sm text-gray-400">Thinking...</span>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Quick Prompts */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 border-t border-[#262626] bg-[#050505]">
              <p className="text-xs text-gray-500 mb-2">Quick help:</p>
              <div className="flex flex-wrap gap-1.5">
                {quickPrompts.slice(0, 4).map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickPrompt(item.prompt)}
                    className="px-2 py-1 text-xs bg-[#171717] border border-[#262626] rounded-full text-gray-400 hover:text-white hover:border-[#00FF94]/30 transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-[#262626]">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Ask me anything..."
                className="flex-1 bg-[#171717] border-[#262626] text-white text-sm placeholder:text-gray-600 h-9"
                disabled={loading}
              />
              <Button 
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                className="bg-[#00FF94] hover:bg-[#00FF94]/90 text-black h-9 w-9 p-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
